import { Injectable } from '@angular/core';

import { ApiService } from '../api/api.service';
import {
  RedemptionCode,
  RedemptionCodeReportFilters,
  RedemptionCodeReportItem,
  RedemptionCodeSet,
  RedemptionCodeSetFilters,
  RedemptionCodeSetProductType,
} from '../../models/redemption-code';
import { ListResponse } from '../../models/list-response';


@Injectable({
  providedIn: 'root'
})
export class RedemptionCodeService {
  constructor(
    private apiService: ApiService
  ) {}

  getRedemptionCodeSets(pageNumber?: number,
                        pageSize?: number,
                        filters?: RedemptionCodeSetFilters): Promise<ListResponse<RedemptionCodeSet>> {
    return this.apiService.getCollection<RedemptionCodeSet, RedemptionCodeSetFilters>(
      'redemption-code-sets',
      pageNumber,
      pageSize,
      filters,
      true
    );
  }

  getRedemptionCodeSet(id: number): Promise<RedemptionCodeSet> {
    return this.apiService.getItem<RedemptionCodeSet>(
      'redemption-code-sets/',
      id
    );
  }

  createRedemptionCodeSetWithAutogeneratedCodes(name: string,
                                                productType: RedemptionCodeSetProductType,
                                                productId: number,
                                                expirationDate: string | null,
                                                numCodes: number): Promise<RedemptionCodeSet> {
    const args: any = {
      name,
      product_type: productType,
      product_id: productId,
      num_codes: numCodes,
    };
    if (expirationDate) {
      args.expiration_date = expirationDate;
    }

    return this.apiService.postToAPI<RedemptionCodeSet>(
      'redemption-code-sets/',
      args
    );
  }

  createRedemptionCodeSetWithCodeList(name: string,
                                      productType: RedemptionCodeSetProductType,
                                      productId: number,
                                      expirationDate: string | null,
                                      codes: string[]): Promise<RedemptionCodeSet> {
    const args: any = {
      name,
      product_type: productType,
      product_id: productId,
      codes,
    };
    if (expirationDate) {
      args.expiration_date = expirationDate;
    }

    return this.apiService.postToAPI<RedemptionCodeSet>(
      'redemption-code-sets/',
      args
    );
  }

  renameRedemptionCodeSet(redemptionCodeSetId: number,
                          name: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/rename/',
      {
        name
      }
    ).then();
  }

  changeRedemptionCodeSetExpiration(redemptionCodeSetId: number,
                                    expirationDate: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/change-expiration/',
      {
        expiration_date: expirationDate
      }
    ).then();
  }

  removeRedemptionCodeSetExpiration(redemptionCodeSetId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/remove-expiration/',
      {}
    ).then();
  }

  expireRedemptionCodeSetImmediately(redemptionCodeSetId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/expire-immediately/',
      {}
    ).then();
  }

  getRedemptionCodes(redemptionCodeSetId: number,
                 pageNumber?: number,
                 pageSize?: number): Promise<ListResponse<RedemptionCode>> {
    return this.apiService.getCollectionNoFilters<RedemptionCode>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/codes/',
      pageNumber,
      pageSize
    );
  }

  getRedemptionCode(redemptionCodeSetId: number,
                    id: number): Promise<RedemptionCode> {
    return this.apiService.getItem<RedemptionCode>(
      'redemption-code-sets/' + redemptionCodeSetId.toString() + '/codes/',
      id
    );
  }

  getRedemptionCodesReport(pageNumber?: number,
                           pageSize?: number,
                           filters?: RedemptionCodeReportFilters): Promise<ListResponse<RedemptionCodeReportItem>> {
    return this.apiService.getCollection<RedemptionCodeReportItem, RedemptionCodeReportFilters>(
      'redemption-codes/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }
}
